import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ArrowLeft, Zap, Plus } from 'lucide-react'
import {
  usePricingPolicy,
  usePolicyVersions,
  usePricingMutations,
} from '@/hooks/usePricing'
import {
  POLICY_STATUS_LABELS,
  VERSION_STATUS_LABELS,
  type ServiceType,
} from '@/types/pricing'
import { RateEditorGrid } from './RateEditorGrid'
import { VersionCreateDialog } from './VersionCreateDialog'

const SERVICE_TABS: { value: ServiceType; label: string }[] = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'ramp_fee', label: 'Ramp Fees' },
  { value: 'parking', label: 'Parking / Hangar' },
  { value: 'service', label: 'Services' },
]

export function PolicyDetailPage() {
  const { policyId } = useParams<{ policyId: string }>()
  const navigate = useNavigate()
  const { data: policy, isLoading: policyLoading } = usePricingPolicy(policyId)
  const { data: versions, isLoading: versionsLoading } = usePolicyVersions(policyId)
  const mutations = usePricingMutations()

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null)
  const [showActivateDialog, setShowActivateDialog] = useState(false)
  const [showVersionDialog, setShowVersionDialog] = useState(false)
  const [serviceTab, setServiceTab] = useState<ServiceType>('fuel')

  const activeVersion = versions?.find((v) => v.status === 'active')
  const currentVersion =
    versions?.find((v) => v.id === selectedVersionId) ?? activeVersion ?? versions?.[0]

  // Auto-select when versions load
  if (versions?.length && !selectedVersionId && currentVersion) {
    // defer to avoid setState during render
    queueMicrotask(() => setSelectedVersionId(currentVersion.id))
  }

  if (policyLoading || versionsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (!policy) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>Policy not found.</p>
        <Button variant="link" onClick={() => navigate('/settings/pricing')}>
          Back to Pricing
        </Button>
      </div>
    )
  }

  const policyStatusVariant =
    policy.status === 'active' ? 'default' : policy.status === 'draft' ? 'secondary' : 'outline'

  const handleActivateVersion = () => {
    if (!currentVersion || !policyId) return
    mutations.activateVersion.mutate(
      { versionId: currentVersion.id, policyId },
      { onSuccess: () => setShowActivateDialog(false) }
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/settings/pricing')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{policy.name}</h1>
            <Badge variant="outline" className="capitalize">
              {policy.scope}
            </Badge>
            <Badge variant={policyStatusVariant}>{POLICY_STATUS_LABELS[policy.status]}</Badge>
          </div>
          {policy.description && (
            <p className="text-muted-foreground mt-1">{policy.description}</p>
          )}
        </div>
      </div>

      {/* Version selector bar */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Rate Version</CardTitle>
            <div className="flex items-center gap-2">
              <Select
                value={currentVersion?.id ?? ''}
                onValueChange={setSelectedVersionId}
              >
                <SelectTrigger className="w-[260px]">
                  <SelectValue placeholder="Select version..." />
                </SelectTrigger>
                <SelectContent>
                  {versions?.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      v{v.versionNumber}
                      {v.label ? ` — ${v.label}` : ''}
                      {v.status === 'active' ? ' (active)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" size="sm" onClick={() => setShowVersionDialog(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" />
                New Version
              </Button>

              {currentVersion && currentVersion.status === 'draft' && (
                <Button
                  size="sm"
                  onClick={() => setShowActivateDialog(true)}
                  disabled={mutations.activateVersion.isPending}
                >
                  <Zap className="mr-1 h-3.5 w-3.5" />
                  Activate
                </Button>
              )}
            </div>
          </div>
          {currentVersion && (
            <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
              <Badge
                variant={
                  currentVersion.status === 'active'
                    ? 'default'
                    : currentVersion.status === 'draft'
                      ? 'secondary'
                      : 'outline'
                }
              >
                {VERSION_STATUS_LABELS[currentVersion.status]}
              </Badge>
              <span>Effective: {currentVersion.effectiveFrom}</span>
              {currentVersion.effectiveTo && <span>to {currentVersion.effectiveTo}</span>}
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Rate editor */}
      {currentVersion ? (
        <Card>
          <CardContent className="pt-6">
            <Tabs value={serviceTab} onValueChange={(v) => setServiceTab(v as ServiceType)}>
              <TabsList>
                {SERVICE_TABS.map((t) => (
                  <TabsTrigger key={t.value} value={t.value}>
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {SERVICE_TABS.map((t) => (
                <TabsContent key={t.value} value={t.value} className="mt-4">
                  <RateEditorGrid
                    versionId={currentVersion.id}
                    serviceType={t.value}
                    readOnly={currentVersion.status !== 'draft'}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p>No versions yet. Create your first version to start adding rates.</p>
            <Button className="mt-4" onClick={() => setShowVersionDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Version
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Activate confirmation dialog */}
      <AlertDialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate Version?</AlertDialogTitle>
            <AlertDialogDescription>
              This will set v{currentVersion?.versionNumber} as the active version. Any
              currently active version will be superseded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleActivateVersion}
              disabled={mutations.activateVersion.isPending}
            >
              {mutations.activateVersion.isPending ? 'Activating...' : 'Activate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Version create dialog */}
      {policyId && (
        <VersionCreateDialog
          open={showVersionDialog}
          onOpenChange={setShowVersionDialog}
          policyId={policyId}
          versions={versions ?? []}
        />
      )}
    </div>
  )
}
